import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormsByCategory } from '../../hooks/useForms';
import { formatDistanceToNow } from 'date-fns';
import DevOnly from '../../components/DevOnly';

const CategoryView = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Prepare query parameters
  const queryParams = {
    search: searchTerm,
    page: page + 1,
    limit: pageSize,
    status: statusFilter !== 'all' ? statusFilter : undefined
  };

  // Fetch forms for this category
  const { data, isLoading, error, refetch } = useFormsByCategory(categoryId, queryParams);

  const categoryNames = {
    'consent': 'Consent Forms',
    'treatment': 'Treatment Forms',
    'questionnaire': 'Questionnaires',
    'lab': 'Lab Forms',
    'prescription': 'Prescriptions',
    'epaper': 'Digital Forms'
  };

  const categoryInfo = data?.category_info;
  const forms = data?.forms || [];
  const totalCount = data?.total || 0;

  const categoryName = categoryInfo?.name
    || categoryNames[categoryId]
    || (categoryId ? `${categoryId.charAt(0).toUpperCase() + categoryId.slice(1)} Forms` : 'Forms');
  const categoryDescription = categoryInfo?.description;
  const totalTemplates = categoryInfo?.templates ?? totalCount;
  const totalSubmissions = categoryInfo?.submissions ?? forms.reduce((sum, form) => sum + (form.submission_count || 0), 0);
  const recentSubmissions = categoryInfo?.recent_submissions ?? forms.reduce((sum, form) => sum + (form.recent_submissions || 0), 0);

  const handleFormClick = (formId) => {
    navigate(`/forms/template/${formId}`);
  };

  const columns = [
    {
      field: 'form_name',
      headerName: 'Form Name',
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          cursor: 'pointer',
          py: 1
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'form_id',
      headerName: 'Form ID',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          #{params.value}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <ActiveIcon fontSize="small" /> : <InactiveIcon fontSize="small" />}
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'submission_count',
      headerName: 'Submissions',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 0}
        </Typography>
      )
    },
    {
      field: 'recent_submissions',
      headerName: '30 Day',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || 0}
        </Typography>
      )
    },
    {
      field: 'created_date',
      headerName: 'Created',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value 
            ? formatDistanceToNow(new Date(params.value), { addSuffix: true })
            : 'Unknown'
          }
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            title="View Form"
            onClick={(e) => {
              e.stopPropagation();
              handleFormClick(params.row.id);
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
          <DevOnly>
            <IconButton
              size="small"
              title="Edit Form"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/forms/edit/${params.row.id}`);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </DevOnly>
          <DevOnly>
            <IconButton
              size="small"
              title="Duplicate Form"
              onClick={(e) => {
                e.stopPropagation();
                // Handle duplicate
              }}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </DevOnly>
        </Box>
      )
    }
  ];

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load forms: {error.message}
          <Button onClick={() => refetch()} size="small" sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/forms" color="inherit">
          Forms
        </Link>
        <Typography color="text.primary">{categoryName}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/forms')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {categoryName}
            </Typography>
            <Chip
              label={`${totalTemplates} template${totalTemplates !== 1 ? 's' : ''}`}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`${totalSubmissions} submission${totalSubmissions !== 1 ? 's' : ''}`}
              color="success"
              variant="outlined"
              size="small"
            />
          </Box>
          {categoryDescription && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
              {categoryDescription}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {recentSubmissions} submission{recentSubmissions !== 1 ? 's' : ''} in the last 30 days.
          </Typography>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/forms/create?category=${categoryId}`)}
          >
            Create Form
          </Button>
        </DevOnly>
      </Box>

      {categoryInfo && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" color="primary">{totalTemplates}</Typography>
              <Typography variant="body2" color="text.secondary">Total Templates</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" color="success.main">{totalSubmissions}</Typography>
              <Typography variant="body2" color="text.secondary">Total Submissions</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" color="info.main">{recentSubmissions}</Typography>
              <Typography variant="body2" color="text.secondary">30 Day Submissions</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search forms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="inactive">Inactive Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Forms DataGrid */}
      <Paper sx={{ height: 'auto', minHeight: 400 }}>
        <DataGrid
          rows={forms}
          columns={columns}
          loading={isLoading}
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          rowCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[25, 50, 100]}
          disableSelectionOnClick
          onRowClick={(params) => handleFormClick(params.id)}
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'grey.50',
              borderBottom: '1px solid',
              borderBottomColor: 'grey.300',
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default CategoryView;
